import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import DocumentationMS from '../../api/documentation';
import { markdownOptions } from '../../helpers/markdownOptions';
import { parseWikiFrontmatter } from '../../helpers/wikiContent';
import useApiQuery from '../../hooks/useApiQuery';
import Modal from '../Modal';

import { useUserData } from '@/redux/selectors';

interface GitlabWikiPageContent {
  title: string;
  content: string;
  format: string;
  slug: string;
}

interface GitlabWikiHelpButtonProps {
  slug: string;
  tooltipMessageId?: string;
  tooltipDefaultMessage?: string;
  modalTitleMessageId?: string;
  modalTitleDefaultMessage?: string;
  buttonAriaLabel?: string;
  buttonSx?: SxProps<Theme>;
  modalWidth?: string;
  modalHeight?: string;
}

const helpButtonBaseSx: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'primary.main',
  mt: 0.5,
};

const helpContentBoxSx: SxProps<Theme> = {
  pr: 1,
};

const loadingContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  py: 6,
};

const helpMarkdownOptions = {
  ...markdownOptions,
  overrides: {
    ...markdownOptions.overrides,
    img: {
      props: {
        style: {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          margin: '16px auto',
          borderRadius: '8px',
        },
      },
    },
  },
} as MarkdownToJSX.Options;

type SxPropsArray = Extract<SxProps<Theme>, readonly unknown[]>;

const isSxPropsArray = (sx: SxProps<Theme>): sx is SxPropsArray => Array.isArray(sx);

const mergeButtonSx = (buttonSx?: SxProps<Theme>): SxProps<Theme> => {
  if (buttonSx === undefined) {
    return helpButtonBaseSx;
  }

  if (isSxPropsArray(buttonSx)) {
    return [helpButtonBaseSx, ...buttonSx];
  }

  return [helpButtonBaseSx, buttonSx];
};

const GitlabWikiHelpButton = ({
  slug,
  tooltipMessageId = '',
  tooltipDefaultMessage = '',
  modalTitleMessageId = '',
  modalTitleDefaultMessage = '',
  buttonAriaLabel,
  buttonSx,
  modalWidth = 'min(960px, 92vw)',
  modalHeight = 'min(80vh, 900px)',
}: GitlabWikiHelpButtonProps) => {
  const intl = useIntl();
  const { locale } = useUserData();
  const resolvedButtonAriaLabel = buttonAriaLabel ?? intl.formatMessage({ id: 'openHelpGuide' });
  const [isOpen, setIsOpen] = useState(false);
  const {
    data: helpContent,
    error: helpContentError,
    isLoading: isHelpContentLoading,
    makeApiCall: getHelpContent,
  } = useApiQuery<GitlabWikiPageContent>(
    DocumentationMS.getGitlabWikiPage.bind(DocumentationMS) as unknown as (
      ...args: unknown[]
    ) => Promise<GitlabWikiPageContent>,
    {
      disableLoader: true,
    }
  );
  const parsedHelpContent = parseWikiFrontmatter(helpContent?.content ?? '');

  // Tracks the (slug, locale) pair the currently-held `helpContent` was
  // fetched for. `helpContent` alone can't tell us that: it stays truthy
  // (just stale) after a locale switch, which previously made the guard
  // below skip refetching forever once content had loaded once.
  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || isHelpContentLoading) {
      return;
    }

    const key = `${slug}::${locale ?? 'en'}`;
    if (helpContent && fetchedKeyRef.current === key) {
      return;
    }

    fetchedKeyRef.current = key;
    void getHelpContent(slug, locale);
  }, [getHelpContent, helpContent, isHelpContentLoading, isOpen, slug, locale]);

  const formatOptionalMessage = (messageId: string, defaultMessage: string): string => {
    if (!messageId && !defaultMessage) {
      return '';
    }

    return intl.formatMessage({
      id: messageId,
      defaultMessage,
    });
  };

  const tooltipLabel = formatOptionalMessage(tooltipMessageId, tooltipDefaultMessage);
  // While a fetch is in flight (including a locale-driven refetch), `helpContent`
  // still holds the previous response - hide the title rather than flashing it.
  const modalTitle = isHelpContentLoading
    ? ''
    : (parsedHelpContent.title ??
      helpContent?.title ??
      formatOptionalMessage(modalTitleMessageId, modalTitleDefaultMessage));
  const resolvedButtonSx = mergeButtonSx(buttonSx);

  return (
    <>
      <Tooltip title={tooltipLabel}>
        <IconButton
          aria-label={resolvedButtonAriaLabel}
          color='primary'
          onClick={() => {
            setIsOpen(true);
          }}
          sx={resolvedButtonSx}
        >
          <HelpOutlineRoundedIcon />
        </IconButton>
      </Tooltip>

      <Modal
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={modalTitle}
        width={modalWidth}
        height={modalHeight}
      >
        <Box sx={helpContentBoxSx}>
          {isHelpContentLoading ? (
            <Box sx={loadingContainerSx}>
              <CircularProgress />
            </Box>
          ) : null}

          {!isHelpContentLoading && helpContentError ? (
            <Typography color='error'>{helpContentError.message}</Typography>
          ) : null}

          {!isHelpContentLoading && helpContent ? (
            <Markdown options={helpMarkdownOptions}>{parsedHelpContent.content}</Markdown>
          ) : null}
        </Box>
      </Modal>
    </>
  );
};

export default GitlabWikiHelpButton;
